/***************************************************************************/
"use client";
/***************************************************************************/

import {
  Alert,
  AlertContent,
  AlertIcon,
  AlertTitle,
} from "@/client/components/ui/alert";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormActions,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
  FormProvider,
} from "@/client/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/client/components/ui/input-otp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { destroyUserLoginSession } from "../actions/login-session";
import { resendOTPAction } from "../actions/otp";
import { TOTPVerificationResult } from "../types";
import ResendTimer from "./resend-timer";

const OtpForm: React.FC<{
  redirectUrl?: string;
  onVerifyOTP: (otp: string) => Promise<TOTPVerificationResult>;
}> = ({ onVerifyOTP, redirectUrl }) => {
  const { t: tV } = useTranslation("v");
  const { t: tAuth } = useTranslation("auth");
  const router = useRouter();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [otpResent, setOtpResent] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);

  const formSchema = z.object({
    otp: z.string().min(6, {
      message: tAuth("otp.validation.required"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      otp: "",
    },
  });

  async function handleOtpValidation(otp: string) {
    setSubmitting(true);
    const result = await onVerifyOTP(otp);
    if (result?.formErrors?.length === 0) {
      router.push(redirectUrl ?? "/");
      await destroyUserLoginSession();
    } else {
      setFormError(
        result?.formErrors?.[0] || tAuth("otp.error.someErrorOccurred"),
      );
      setSubmitting(false);
    }
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    handleOtpValidation(values.otp);
  }

  async function handleResendOTP() {
    setResendLoading(true);
    const result = await resendOTPAction();
    form.reset();
    if (result.status) {
      setOtpResent(true);
    } else if (result.formErrors) {
      setFormError(
        result.formErrors[0] || tAuth("otp.error.someErrorOccurred"),
      );
    }
    setResendLoading(false);
  }

  async function clearLoginSession() {
    await destroyUserLoginSession();
  }

  return (
    <Card size="lg" className="w-[340px]">
      <CardHeader>
        <CardTitle className="flex items-center">
          <span className="capitalize">{tAuth("otp.title")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <Form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem>
                  <FormDescription>{tAuth("otp.description")}</FormDescription>
                  <FormControl>
                    <InputOTP
                      maxLength={6}
                      value={field.value}
                      onChange={field.onChange}
                      inputMode="text"
                      pattern="[A-Za-z0-9]*"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormActions className="block">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting}
                loading={submitting}
                className="mb-3"
              >
                <span>{tV("verify")}</span>
              </Button>
              <ResendTimer
                onResendOTP={handleResendOTP}
                initialTime={60}
                resendLoading={resendLoading}
              />
            </FormActions>
            {otpResent && (
              <Alert variant="success" className="flex items-center">
                <AlertIcon />
                <AlertContent>
                  <AlertTitle>{tAuth("otp.resend.successMessage")}</AlertTitle>
                </AlertContent>
              </Alert>
            )}
            {!submitting && formError && (
              <Alert variant="error">
                <AlertIcon />
                <AlertContent>
                  <AlertTitle>{formError}</AlertTitle>
                </AlertContent>
              </Alert>
            )}
          </Form>
        </FormProvider>
      </CardContent>
      <CardFooter className="text-center p-0">
        <Button
          onClick={clearLoginSession}
          className="capitalize border border-gray-150 border-l-0 border-r-0 border-b-0 border-t-1 w-full rounded-t-none p-3"
          variant="link"
        >
          {tAuth("otp.changeMail")}
        </Button>
      </CardFooter>
    </Card>
  );
};

export { OtpForm };
